import { useEffect } from 'react';
import { toast } from 'sonner';
import { AlertCircle, Copy } from 'lucide-react';
import { useOperatorStore } from '@/core/stores/operator-store';
import { useAuthStore } from '@/core/stores/auth-store';
import { useOcpiSettingsStore } from './stores/ocpi-settings-store';
import { ConnectionStatusDashboard } from './components/organisms/ConnectionStatusDashboard';
import { OnboardingForm } from './components/organisms/OnboardingForm';
import { ActorSelector } from '@/core/components/ActorSelector';
import { Button } from '@/core/components/ui/button';
import { copyToClipboard } from '@/core/utils/clipboard';
import type { ExternalCpoConnectionRequestDto, VpostConnectionRequestDto } from './types/api-types';
import type { OcpiModule } from './use-cases/SyncCpoOcpiDataUseCase';
import { getPlainLanguageFailureReason, formatAlertTime } from './utils/error-messages';

/**
 * Copy error to clipboard with context for support
 */
const copyErrorToClipboard = async (error: string, operatorId?: number, operatorName?: string) => {
  const timestamp = new Date().toISOString();
  const errorReport = `OCPI Admin Error Report
========================
Timestamp: ${timestamp}
Operator ID: ${operatorId || 'N/A'}
Operator Name: ${operatorName || 'N/A'}
URL: ${window.location.href}

Error Message:
${error}
========================`;

  await copyToClipboard(errorReport, 'Error details copied to clipboard');
};

export function OcpiSettings() {
  const { selectedOperator, operators, isLoading: operatorsLoading, loadOperators, selectOperator } = useOperatorStore();
  const { isDistributor } = useAuthStore();
  const {
    connectionStatus,
    cpoType,
    isLoadingStatus,
    isRefreshing,
    isPublishingLocations,
    isPublishingTariffs,
    isSyncingEves,
    isSettingUpKiosk,
    isOnboarding,
    isSettingUpExternalCpo,
    isSettingUpVpost,
    publishLocationsResult,
    publishTariffsResult,
    syncEvesSuccess,
    error,
    needsOnboarding,
    checkConnectionStatus,
    publishLocations,
    publishTariffs,
    syncEves,
    setupKioskConnection,
    onboardOperator,
    setupExternalCpoConnection,
    setupVpostConnection,
    loadCsmsNames,
    loadKioskCsmsNames,
    syncCpoOcpiData,
    syncingCpoModules,
    clearLocationsResult,
    clearTariffsResult,
    clearSyncEvesResult,
    clearPublishResults,
    clearError,
  } = useOcpiSettingsStore();

  // Load operators on mount for distributors
  useEffect(() => {
    if (isDistributor && operators.length === 0 && !operatorsLoading) {
      loadOperators();
    }
  }, [isDistributor, operators.length, operatorsLoading, loadOperators]);

  // Load connection status on mount or when operator changes
  useEffect(() => {
    if (selectedOperator?.operatorId && selectedOperator?.name) {
      checkConnectionStatus(selectedOperator.operatorId, selectedOperator.name);
    }
  }, [selectedOperator?.operatorId, selectedOperator?.name, checkConnectionStatus]);

  const handleOperatorChange = (operatorId: string) => {
    const selectedOp = operators.find(op => op.operatorId.toString() === operatorId);
    if (selectedOp) {
      // Clear publish results from previous operator before switching
      clearPublishResults();
      selectOperator(selectedOp);
    }
  };

  const handleRefresh = async () => {
    if (!selectedOperator?.operatorId || !selectedOperator?.name) {
      toast.error('No operator selected');
      return;
    }

    try {
      await checkConnectionStatus(selectedOperator.operatorId, selectedOperator.name);
      toast.success('Connection data refreshed successfully');
    } catch {
      toast.error('Failed to refresh connection data');
    }
  };

  const handlePublishLocations = async () => {
    if (!selectedOperator?.operatorId) {
      toast.error('No operator selected');
      return;
    }

    const operatorName = selectedOperator.name;
    const outcome = await publishLocations(selectedOperator.operatorId);

    if (!outcome.ok) {
      // Request itself failed (network/timeout/etc.)
      toast.error('Locations were not published', {
        description: getPlainLanguageFailureReason(outcome.error, outcome.status),
      });
      return;
    }

    const result = outcome.data;
    const time = formatAlertTime(result.timestamp);
    if (result.isSuccess) {
      toast.success(`Locations published for ${operatorName}`, {
        description: `${result.successCount} machine(s) updated at ${time}.`,
      });
    } else if (result.successCount > 0) {
      // Add the reason only when the backend sent one (errorSummary otherwise just repeats the failed count)
      const reason = result.response.errorMessage ? ` ${getPlainLanguageFailureReason(result.response.errorMessage)}` : '';
      toast.warning(`Locations partially published for ${operatorName}`, {
        description: `${result.successCount} machine(s) updated, ${result.failedCount} failed at ${time}.${reason}`,
      });
    } else {
      toast.error(`Locations were not published for ${operatorName}`, {
        description: getPlainLanguageFailureReason(result.errorSummary),
      });
    }
  };

  const handlePublishTariffs = async () => {
    if (!selectedOperator?.operatorId) {
      toast.error('No operator selected');
      return;
    }

    const operatorName = selectedOperator.name;
    const outcome = await publishTariffs(selectedOperator.operatorId);

    if (!outcome.ok) {
      toast.error('Tariffs were not published', {
        description: getPlainLanguageFailureReason(outcome.error, outcome.status),
      });
    } else if (outcome.data.isSuccess) {
      toast.success(`Tariffs published for ${operatorName}`, {
        description: `Updated at ${formatAlertTime(outcome.data.timestamp)}.`,
      });
    } else {
      toast.error(`Tariffs were not published for ${operatorName}`, {
        description: getPlainLanguageFailureReason(outcome.data.errorSummary),
      });
    }
  };

  const handleSyncEves = async (publishToOcpi: boolean) => {
    if (!selectedOperator?.operatorId) {
      toast.error('No operator selected');
      return false;
    }
    const outcome = await syncEves(selectedOperator.operatorId, publishToOcpi);
    if (outcome.ok) {
      toast.success(`Data synced for ${selectedOperator.name}`, {
        description: `Synced at ${formatAlertTime()}${publishToOcpi ? ' and published to OCPI' : ''}.`,
      });
    } else {
      toast.error(`Data sync failed for ${selectedOperator.name}`, {
        description: getPlainLanguageFailureReason(outcome.error, outcome.status),
      });
    }
    return outcome.ok;
  };

  const handleSetupKiosk = async () => {
    if (!selectedOperator?.operatorId || !selectedOperator?.name) {
      toast.error('No operator selected');
      return;
    }

    try {
      await setupKioskConnection(selectedOperator.operatorId, selectedOperator.name);
      toast.success('Kiosk connection setup successfully');
    } catch {
      toast.error('Failed to setup Kiosk connection');
    }
  };

  const handleOnboardInternal = async (partyId: string, countryCode: string, name: string) => {
    if (!selectedOperator?.operatorId) {
      toast.error('No operator selected');
      return;
    }

    const success = await onboardOperator(selectedOperator.operatorId, partyId, countryCode, name);
    if (success) {
      toast.success('Operator successfully configured as Internal CPO');
    }
  };

  const handleOnboardExternal = async (data: ExternalCpoConnectionRequestDto) => {
    if (!selectedOperator?.operatorId) {
      toast.error('No operator selected');
      return;
    }

    const success = await setupExternalCpoConnection(
      selectedOperator.operatorId,
      selectedOperator.name,
      data
    );
    if (success) {
      toast.success('External CPO connection configured successfully');
    }
  };

  const handleOnboardExternalVpost = async (data: VpostConnectionRequestDto) => {
    if (!selectedOperator?.operatorId) {
      toast.error('No operator selected');
      return;
    }

    const success = await setupVpostConnection(
      selectedOperator.operatorId,
      selectedOperator.name,
      data
    );
    if (success) {
      toast.success('OCPI-to-VPOST connection configured successfully');
    }
  };

  const handleSyncCpoModule = async (connectionId: number, ocpiModule: OcpiModule) => {
    if (!selectedOperator?.operatorId) {
      toast.error('No operator selected');
      return;
    }
    const outcome = await syncCpoOcpiData(selectedOperator.operatorId, connectionId, ocpiModule);
    const moduleLabel = ocpiModule.charAt(0).toUpperCase() + ocpiModule.slice(1);

    if (!outcome.ok) {
      toast.error(`Failed to sync ${ocpiModule} for ${selectedOperator.name}`, {
        description: `${getPlainLanguageFailureReason(outcome.error, outcome.status)} Manual intervention is required.`,
      });
    } else if (outcome.data.fellBack) {
      toast.warning(`${moduleLabel} synced for ${selectedOperator.name} with a full sync`, {
        description: `Incremental sync failed ${outcome.data.incrementalAttempts} times. Full sync completed at ${formatAlertTime()}.`,
      });
    } else {
      const attemptNote =
        outcome.data.incrementalAttempts > 1 ? ` after ${outcome.data.incrementalAttempts} attempts` : '';
      toast.success(`${moduleLabel} synced for ${selectedOperator.name}`, {
        description: `Incremental sync completed at ${formatAlertTime()}${attemptNote}.`,
      });
    }
  };

  // Handler for external kiosk setup from the dashboard
  const handleSetupExternalKiosk = async (data: ExternalCpoConnectionRequestDto) => {
    if (!selectedOperator?.operatorId) {
      toast.error('No operator selected');
      return;
    }

    const success = await setupExternalCpoConnection(
      selectedOperator.operatorId,
      selectedOperator.name,
      data
    );
    if (success) {
      toast.success('Kiosk connection setup successfully');
    }
  };

  const handleLoadCsmsNames = async (): Promise<string[]> => {
    return loadCsmsNames();
  };

  // Handler for OCPI-to-VPOST setup from the dashboard
  const handleSetupVpost = async (data: VpostConnectionRequestDto) => {
    if (!selectedOperator?.operatorId) {
      toast.error('No operator selected');
      return;
    }

    const success = await setupVpostConnection(
      selectedOperator.operatorId,
      selectedOperator.name,
      data
    );
    if (success) {
      toast.success('OCPI-to-VPOST connection setup successfully');
    }
  };

  // No operator selected state
  if (!selectedOperator) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center min-h-[50vh] space-y-4">
          <AlertCircle className="h-16 w-16 text-gray-400" aria-hidden="true" />
          <h2 className="text-2xl font-semibold text-gray-900">No Operator Selected</h2>
          <p className="text-gray-600 text-center max-w-md">
            Please select an operator from the header to view OCPI connection settings.
          </p>
        </div>
      </div>
    );
  }

  // Loading state (only show if not onboarding)
  if (isLoadingStatus && !connectionStatus && !needsOnboarding) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white border rounded-lg p-6">
          <h1 className="text-xl font-semibold text-gray-900 mb-6">OCPI Admin</h1>
          <div className="flex flex-col items-center justify-center min-h-[40vh] space-y-4">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#FFCD00]" />
            <p className="text-gray-600">Loading connection status...</p>
          </div>
        </div>
      </div>
    );
  }

  // Success state with data
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="space-y-6">
        {/* Page header */}
        <div className="border-l-4 border-yellow-400 pl-4">
          <h1 className="text-xl font-semibold text-gray-900">OCPI Admin</h1>
          <p className="text-gray-600 text-sm">
            Manage OCPI connections and view service status
          </p>
        </div>

        {/* Operator selector for distributors */}
        {isDistributor && operators.length > 0 && (
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Operator</label>
            <ActorSelector
              operators={operators}
              selectedOperatorId={selectedOperator?.operatorId.toString() || ''}
              onOperatorChange={handleOperatorChange}
              isLoading={operatorsLoading}
              disabled={isPublishingLocations || isPublishingTariffs || isSyncingEves}
              className="w-80"
            />
          </div>
        )}

        {/* Show warning if there's an error but we have cached data */}
        {error && connectionStatus && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-yellow-800">Warning</p>
              <p className="text-sm text-yellow-700 break-words">{error}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyErrorToClipboard(error, selectedOperator?.operatorId, selectedOperator?.name)}
                title="Copy error details for support"
              >
                <Copy className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={clearError}>
                Dismiss
              </Button>
            </div>
          </div>
        )}

        {/* Show onboarding form when operator not configured */}
        {needsOnboarding && !connectionStatus && (
          <OnboardingForm
            operatorName={selectedOperator.name}
            currency={selectedOperator.currency}
            onSubmitInternal={handleOnboardInternal}
            onSubmitExternal={handleOnboardExternal}
            onSubmitExternalVpost={handleOnboardExternalVpost}
            onLoadCsmsNames={handleLoadCsmsNames}
            onLoadKioskCsmsNames={loadKioskCsmsNames}
            isLoadingInternal={isOnboarding}
            isLoadingExternal={isSettingUpExternalCpo}
            isLoadingExternalVpost={isSettingUpVpost}
            error={error}
          />
        )}

        {/* Show error state when no connections could be loaded (and not onboarding) */}
        {error && !connectionStatus && !needsOnboarding && (
          <div className="bg-white border rounded-lg p-8">
            <div className="text-center mb-6">
              <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Connections</h3>
            </div>

            {/* Error details box */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 max-w-2xl mx-auto">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-red-800 mb-1">Error Details</p>
                  <p className="text-sm text-red-700 break-words font-mono whitespace-pre-wrap">{error}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyErrorToClipboard(error, selectedOperator.operatorId, selectedOperator.name)}
                  className="flex-shrink-0 border-red-300 text-red-700 hover:bg-red-100"
                  title="Copy error details for support"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </Button>
              </div>
            </div>

            <div className="text-center">
              <Button
                onClick={() => checkConnectionStatus(selectedOperator.operatorId, selectedOperator.name)}
                variant="outline"
              >
                Retry
              </Button>
            </div>
          </div>
        )}

        {/* Dashboard */}
        {connectionStatus && (
          <ConnectionStatusDashboard
            connectionStatus={connectionStatus}
            cpoType={cpoType}
            onRefresh={handleRefresh}
            onPublishLocations={handlePublishLocations}
            onPublishTariffs={handlePublishTariffs}
            onSyncEves={handleSyncEves}
            onSetupKiosk={handleSetupKiosk}
            onSetupExternalKiosk={handleSetupExternalKiosk}
            onSetupVpost={handleSetupVpost}
            onLoadCsmsNames={handleLoadCsmsNames}
            onLoadKioskCsmsNames={loadKioskCsmsNames}
            onSyncCpoModule={handleSyncCpoModule}
            syncingCpoModules={syncingCpoModules}
            onClearLocationsResult={clearLocationsResult}
            onClearTariffsResult={clearTariffsResult}
            onClearSyncEvesResult={clearSyncEvesResult}
            isLoading={isLoadingStatus}
            isRefreshing={isRefreshing}
            isPublishingLocations={isPublishingLocations}
            isPublishingTariffs={isPublishingTariffs}
            isSyncingEves={isSyncingEves}
            isSettingUpKiosk={isSettingUpKiosk}
            isSettingUpExternalKiosk={isSettingUpExternalCpo}
            isSettingUpVpost={isSettingUpVpost}
            publishLocationsResult={publishLocationsResult}
            publishTariffsResult={publishTariffsResult}
            syncEvesSuccess={syncEvesSuccess}
            externalKioskError={cpoType === 'external' ? error : null}
            vpostError={cpoType === 'external' ? error : null}
          />
        )}
      </div>
    </div>
  );
}
